import { Button, createTheme, Dialog, Stack, ThemeProvider, Typography } from '@mui/material';
import { type FC, useEffect, useState } from 'react';
import * as yup from "yup";
import { useAlert } from '../../contexts/AlertProvider';
import FormProvider, { useForm } from '../../helpers/forms/FormProvider';
import { BasicButton } from '../generic-components/button/Button';
import { FormTextField } from '../form-components/form-text-field/FormTextField';
import { provide } from '../../helpers/wrap-and-provide';
import { statusRepo } from '@repositories';
import { useOrganization } from '../../contexts/OrganizationProvider';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { GeneralAlert } from '../alert/GeneralAlert';
import { type Status } from '@entities';
import useQuestion from '../../dialog-components/QuestionDialog';

interface StatusDialogProps {
    open: boolean;
    onClose: () => void;
    type: "volunteer" | "mission" | "group";
}

const validationSchema = yup.object().shape({
    title: yup.string().required("שדה חובה"),
    order: yup.number(),
    color: yup.string().required("שדה חובה"),
});

type FormType = yup.InferType<typeof validationSchema>;

export const StatusDialog: FC<StatusDialogProps> = ({ open, onClose, type }) => {
    const { currOrganization, getCurrOrganization } = useOrganization();
    const initialStatuses = currOrganization?.status
        ?.filter((status) => status.type === type.toUpperCase() as "GROUP" | "VOLUNTEER" | "MISSION")
        ?.sort((a, b) => a.order - b.order) || [];
    const [statuses, setStatuses] = useState(initialStatuses);
    const { setAlertText, setSeverity, setTime } = useAlert();
    const [isAdd, setIsAdd] = useState<boolean>(false);
    const { resetForm, values, isValid, setValues } = useForm<FormType>();
    const [isLoading, setIsLoading] = useState(false);
    const [isStatusChanged, setIsStatusChanged] = useState<Boolean>(false);
    const [status, setStatus] = useState<Status>();
    const question = useQuestion();

    useEffect(() => {
        if (status) {
            setValues({
                title: status.title,
                order: status.order,
                color: status.color,
            });
        }
    }, [status]);

    useEffect(() => {
        setStatuses(initialStatuses);
    }, [currOrganization?.status]);

    useEffect(() => {
        setValues({ ...(values as any), type });
    }, [type]);

    const handleDragEnd = (
        result: DropResult,
    ): void => {
        if (!result.destination) return;

        // Reorder the statuses array
        const reorderedStatuses = Array.from(statuses);
        const [movedStatus] = reorderedStatuses.splice(result.source.index, 1);
        reorderedStatuses.splice(result.destination.index, 0, movedStatus);

        // Update state
        setStatuses(reorderedStatuses);
        setIsStatusChanged(true);
    };

    const onOrderSave = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const updatedStatuses = statuses.map((status, index) => ({
                ...status,
                order: index,
            }));
            await Promise.all(updatedStatuses.map(status => statusRepo.update(status.id, status)));
            getCurrOrganization();
            setAlertText("הסדר נשמר בהצלחה");
            setSeverity("success");
            setTime(3000);
            setIsStatusChanged(false);
        } catch (error) {
            setAlertText("שגיאה בשמירת הסדר");
            setSeverity("error");
            setTime(3000);
        } finally {
            setIsLoading(false);
        }
    };

    const saveStatus = async () => {
        if (!isValid) return;
        const values1: Record<string, string> = values as {};
        if (isLoading) return;
        try {
            if (status) {
                await statusRepo.update(status.id, { ...values1 });
                resetForm();
                await getCurrOrganization();
                setIsAdd(false);
                setStatus(undefined);
                return;
            } else {
                await statusRepo.insert({ ...values1, type: type.toUpperCase() as "GROUP" | "VOLUNTEER" | "MISSION", organization: currOrganization });
                resetForm();
                getCurrOrganization();
                setIsLoading(false);
                setIsAdd(false);
            }
        } catch (error) {
            setIsLoading(false);
            setSeverity("error");
            setTime(3000);
        } finally {
            setIsLoading(false);
        }
    };

    const colors = [
        { content: "טורקיז בוהק", value: "#00E5FF" }, // Bright Turquoise
        { content: "תכלת קרח", value: "#33CCFF" }, // Ice Blue
        { content: "תכלת שמיים", value: "#80DFFF" }, // Light Sky Blue
        { content: "כחול קלאסי", value: "#0066FF" }, // Classic Blue (darker)
        { content: "כחול רויאל", value: "#0036D3" }, // Royal Blue (darker)
        { content: "כחול נייבי", value: "#000080" }, // Navy Blue (brighter)
        { content: "כחול עמוק", value: "#002366" }, // Brighter Deep Blue
        { content: "סגול רך", value: "#7A5EFF" }, // Soft Purple
        { content: "סגול כהה", value: "#4B0082" }, // Indigo
        { content: "סגול מלכותי", value: "#6A0DAD" }, // Royal Purple
        { content: "טורקיז ים כהה", value: "#008B8B" } // Dark Sea Turquoise
    ];

    const deleteStatus = async () => {
        if (isLoading) return;
        question("האם ברצונך למחוק סטטוס זה?").then(async (yes) => {
            if (!yes) return;
            try {
                await statusRepo.delete(status?.id || "111");
                await getCurrOrganization();
                setIsAdd(false);
                setStatus(undefined);
            } catch (error) {
                setIsLoading(false);
                setSeverity("error");
                setTime(3000);
            } finally {
                setIsLoading(false);
            }
        });
    };

    const theme = createTheme({
        palette: {
            primary: {
                main: "#00b4d8",
            },
            secondary: {
                main: "#05204CF2",
            },
        },
    });

    return (
        <ThemeProvider theme={theme}>
            <Dialog
                hideBackdrop
                PaperProps={{
                    sx: {
                        bgcolor: "white",
                        borderRadius: "35px",
                        padding: "2rem",
                        width: "30vw",
                        maxWidth: "unset",
                        minHeight: "30vh",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "center",
                    },
                }}
                open={open}
                onClose={() => {
                    onClose();
                    setIsAdd(false);
                    setStatus(undefined);
                }}
                className="group-drawer add-group-dialog"
            >
                {isAdd || status ? <>
                    <Stack className="field-container">
                        <FormTextField name="title" label="כותרת" />
                        <FormTextField name="order" label="סדר" type='number' />
                        <Stack flexWrap="wrap" flexDirection="row" gap="0.5rem" margin="1rem auto">
                            {colors.map((color, index) => {
                                return <div key={index}
                                    style={{ backgroundColor: color.value, width: "30px", height: "30px", display: "flex", justifyContent: "center", alignItems: "center", }}
                                    onClick={() => setValues({ ...values, color: color.value })}
                                >
                                    {values.color === color.value && <CheckIcon style={{ color: "white" }} />}
                                </div>;
                            })}
                        </Stack>
                    </Stack>
                    <Stack flexDirection="row" gap="2rem" sx={{ backgroundColor: "transparent" }} width="50%">
                        <BasicButton
                            text={status ? "עריכה" : "הוספה"}
                            type="submit"
                            isProgress={isLoading}
                            onClick={saveStatus}
                        />
                        {status && <BasicButton
                            text="מחיקה"
                            type="button"
                            isProgress={isLoading}
                            onClick={deleteStatus}
                        />}
                    </Stack>
                </> : <>
                    <Typography style={{ textAlign: "center", fontSize: "1.2rem", marginBottom: "1.5rem" }} className="title">סטטוסים פעילים</Typography>
                    <Button
                        onClick={() => setIsAdd(true)}
                        sx={{ gap: "0.3vw", borderRadius: "1rem", width: "fit-content", position: "absolute", top: "1rem", left: "1rem" }}
                        color="secondary"
                        variant="outlined"
                    >
                        הוספת סטטוס
                    </Button>
                    {isStatusChanged && <Button
                        onClick={onOrderSave}
                        sx={{ gap: "0.3vw", borderRadius: "1rem", width: "fit-content", position: "absolute", bottom: "1rem", right: "1rem" }}
                        color="secondary"
                        variant="outlined"
                    >
                        שמירת סדר
                    </Button>}
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="statuses">
                            {(provided) => (
                                <Stack
                                    width="fit-content"
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {statuses.map((status, index) => (
                                        <Draggable key={status.id} draggableId={String(status.id)} index={index}>
                                            {(provided) => (
                                                <Stack
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className="field-container"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    minWidth="10vw"
                                                    paddingInline="0.3rem"
                                                    marginY={1}
                                                    width="fit-content"
                                                    onClick={() => setStatus(status)}
                                                >
                                                    {index > 0 && <KeyboardArrowDownIcon />}
                                                    <Stack
                                                        className="field-container"
                                                        flexDirection="row"
                                                        justifyContent="center"
                                                        padding="0.5rem 0"
                                                        style={{
                                                            backgroundColor: status.color,
                                                            color: "white",
                                                            borderRadius: "6px",
                                                            textAlign: "center",
                                                        }}
                                                    >
                                                        <Typography>{status.title}</Typography>
                                                    </Stack>
                                                </Stack>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </Stack>
                            )}
                        </Droppable>
                    </DragDropContext>
                </>}
                {statuses.length > 5 && <GeneralAlert />}
            </Dialog >
        </ThemeProvider>
    );
};

export default provide([FormProvider, {
    initialValues: {
        order: 0,
        title: "",
        type: "",
        color: "",
    },
    className: "status-form",
    validationSchema,
    onSubmit: () => { }
}])(StatusDialog);
